import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  createBillingCycle,
  createInvoiceAdjustment,
  exportMemberArrears,
  generateInvoices,
  getArrearsAgingSummary,
  getInvoice,
  getInvoiceReading,
  getMemberBalance,
  listBillingCycles,
  listGenerationRuns,
  listInvoices,
  listMemberArrears,
  listMemberInvoices,
  markDraftInvoicesAsSent,
  markInvoiceAsSent,
  voidInvoice,
  type ArrearsParams,
  type CreateBillingCycleInput,
  type CreateInvoiceAdjustmentInput,
  type GenerateInvoicesInput,
  type InvoiceFilters,
  type MarkDraftInvoicesAsSentInput,
  type MemberInvoiceFilters,
} from '@/lib/billing'

export function useGenerateInvoices(utilityTypeId: string, billingPeriod: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: GenerateInvoicesInput) => generateInvoices(input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['meter-readings', utilityTypeId, billingPeriod] })
      qc.invalidateQueries({ queryKey: ['invoices'] })
      qc.invalidateQueries({ queryKey: ['arrears'] })
      qc.invalidateQueries({ queryKey: ['billing-runs'] })
    },
  })
}

export function useGenerationRuns() {
  return useQuery({ queryKey: ['billing-runs'], queryFn: listGenerationRuns })
}

export function useBillingCycles() {
  return useQuery({ queryKey: ['billing-cycles'], queryFn: listBillingCycles })
}

export function useCreateBillingCycle() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CreateBillingCycleInput) => createBillingCycle(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['billing-cycles'] }),
  })
}

export function useInvoices(filters: InvoiceFilters) {
  return useQuery({
    queryKey: ['invoices', filters],
    queryFn: () => listInvoices(filters),
  })
}

/** Credit notes and supplementary charges raised against one invoice, and its adjusted total. */
export function useInvoiceAdjustments(invoice: { id: string; memberId: string; amount: number }) {
  const { data, isLoading } = useInvoices({
    memberId: invoice.memberId,
    excludeInvoiceType: 'standard',
    limit: 100,
  })
  const adjustments = (data?.data ?? []).filter((inv) => inv.relatedInvoiceId === invoice.id)
  const adjustmentsTotal =
    Math.round(
      adjustments.filter((a) => a.status !== 'voided').reduce((sum, a) => sum + a.amount, 0) * 100,
    ) / 100
  const adjustedTotal = Math.round((invoice.amount + adjustmentsTotal) * 100) / 100
  return { adjustments, adjustmentsTotal, adjustedTotal, isLoading }
}

export function useInvoice(id: string | null) {
  return useQuery({
    queryKey: ['invoices', 'detail', id],
    queryFn: () => getInvoice(id!),
    enabled: id !== null,
  })
}

export function useInvoiceReading(id: string | null) {
  return useQuery({
    queryKey: ['invoices', 'reading', id],
    queryFn: () => getInvoiceReading(id!),
    enabled: id !== null,
  })
}

export function useMarkInvoiceAsSent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => markInvoiceAsSent(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invoices'] }),
  })
}

export function useMarkDraftInvoicesAsSent() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: MarkDraftInvoicesAsSentInput) => markDraftInvoicesAsSent(input),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['invoices'] }),
  })
}

export function useMemberInvoices(filters: MemberInvoiceFilters = {}) {
  return useQuery({
    queryKey: ['member-invoices', filters],
    queryFn: () => listMemberInvoices(filters),
  })
}

export function useMemberArrears(params: ArrearsParams) {
  return useQuery({
    queryKey: ['arrears', params],
    queryFn: () => listMemberArrears(params),
  })
}

export function useArrearsAgingSummary() {
  return useQuery({
    queryKey: ['arrears-aging-summary'],
    queryFn: () => getArrearsAgingSummary(),
  })
}

/** Top N members by outstanding balance, for the "biggest debtors" chart — reuses the export
 * endpoint (already sorted desc by outstandingAmount) rather than adding a dedicated route. */
export function useTopArrears(limit: number, enabled = true) {
  return useQuery({
    queryKey: ['arrears-top', limit],
    queryFn: async () => {
      const { data } = await exportMemberArrears()
      return data.slice(0, limit)
    },
    enabled,
  })
}

export function useVoidInvoice() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ id, reason }: { id: string; reason?: string }) => voidInvoice(id, reason),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] })
      qc.invalidateQueries({ queryKey: ['approvals'] })
    },
  })
}

export function useCreateInvoiceAdjustment() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: ({ invoiceId, ...input }: CreateInvoiceAdjustmentInput & { invoiceId: string }) =>
      createInvoiceAdjustment(invoiceId, input),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['invoices'] })
      qc.invalidateQueries({ queryKey: ['approvals'] })
    },
  })
}

export function useMemberBalance(memberId?: string) {
  return useQuery({
    queryKey: ['member-balance', memberId ?? 'self'],
    queryFn: () => getMemberBalance(memberId),
  })
}
